import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

export function SipSettings({ settings, save }) {
  const existing = settings.sip || {};
  const parsed = (() => {
    if (existing.server || existing.login) return {};
    const m = /^sip:([^@]+)@(.+)$/.exec(existing.uri || "");
    return m ? { login: m[1], server: m[2] } : {};
  })();
  const [cfg, setCfg] = useState({ enabled: false, mode: "direct", server: "", operator_server: "", public_ip: "", login: "", password: "", ws_url: "", display_name: "", call_flyto: true, ...existing, ...parsed });
  const [adv, setAdv] = useState(false);
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState(null);
  const [turnResult, setTurnResult] = useState(null);
  const [iceLog, setIceLog] = useState(null);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const submit = async () => {
    const server = (cfg.server || "").trim().replace(/^sips?:\/\//, "").replace(/\/.*$/, "");
    const login = (cfg.login || "").trim();
    const operator_server = (cfg.operator_server || "").trim().replace(/^sips?:\/\//, "").replace(/\/.*$/, "");
    if (cfg.enabled && (!server || !login)) { toast.error("Podaj serwer SIP i login"); return; }
    if (cfg.enabled && cfg.mode === "gateway" && !operator_server) { toast.error("Podaj serwer operatora (PBX)"); return; }
    const uri = login && server ? `sip:${login}@${server}` : "";
    const ws_url = (cfg.ws_url || "").trim() || (server ? `wss://${server}:8089/ws` : "");
    const payload = { enabled: cfg.enabled, mode: cfg.mode || "direct", server, operator_server, public_ip: (cfg.public_ip || "").trim(), login, password: cfg.password || "",
                      uri, ws_url, display_name: (cfg.display_name || "").trim() || login,
                      call_flyto: cfg.call_flyto !== false,
                      turn_host: cfg.turn_host || "", turn_user: cfg.turn_user || "", turn_pass: cfg.turn_pass || "" };
    setBusy(true);
    try { await save({ sip: payload }); setCfg((c) => ({ ...c, uri, ws_url })); toast.success("Zapisano konto SIP"); }
    catch { toast.error("Błąd zapisu"); }
    finally { setBusy(false); }
  };
  const applyGateway = async () => {
    await submit();
    setBusy(true);
    try {
      const { data } = await api.post("/sip/gateway-apply");
      if (data.restarted) toast.success("Bramka skonfigurowana i uruchomiona ✔");
      else toast.message("Zapisano konfigurację bramki. Uruchom ją: docker compose up -d" + (data.detail ? ` — ${data.detail}` : ""), { duration: 14000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Nie udało się skonfigurować bramki"); }
    finally { setBusy(false); }
  };
  const showLogs = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/sip/gateway-logs");
      setLogs(data.logs || "(brak)");
    } catch (e) { toast.error(e.response?.data?.detail || "Nie udało się pobrać logów"); }
    finally { setBusy(false); }
  };
  const enableDebug = async () => {
    setBusy(true);
    try {
      await api.post("/sip/gateway-debug");
      toast.success("Debug włączony. Wykonaj połączenie testowe, potem kliknij „Pokaż logi bramki”.", { duration: 10000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Nie udało się włączyć debugu"); }
    finally { setBusy(false); }
  };
  const testTurn = async () => {
    const host = (cfg.turn_host || "").trim(), tuser = (cfg.turn_user || "").trim(), tpass = (cfg.turn_pass || "");
    if (!host || !tuser) { setTurnResult("Brak konfiguracji TURN w słuchawce. Kliknij „Zapisz i skonfiguruj bramkę”, potem ODŚWIEŻ stronę (F5) i spróbuj ponownie."); return; }
    setTurnResult("Testuję TURN (do 8 s)…");
    try {
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: [`turn:${host}:3478?transport=udp`, `turn:${host}:3478?transport=tcp`], username: tuser, credential: tpass }],
        iceTransportPolicy: "relay",
      });
      pc.createDataChannel("t");
      const types = new Set();
      let relay = false;
      pc.onicecandidate = (e) => {
        if (e.candidate && e.candidate.candidate) {
          const m = /typ (\w+)/.exec(e.candidate.candidate);
          if (m) { types.add(m[1]); if (m[1] === "relay") relay = true; }
        }
      };
      await pc.setLocalDescription(await pc.createOffer());
      await new Promise((r) => setTimeout(r, 8000));
      try { pc.close(); } catch { /* noop */ }
      if (relay) setTurnResult(`✔ TURN DZIAŁA — przeglądarka uzyskała kandydat „relay” z ${host}. Audio przychodzące powinno teraz działać. Zadzwoń ponownie.`);
      else setTurnResult(`✗ TURN NIE działa — brak kandydata „relay” (uzyskane typy: ${[...types].join(", ") || "żadne"}). Sprawdź na serwerze: 1) czy kontener fiberpass-turn działa (docker ps), 2) czy porty 3478/udp+tcp i 49152-49200/udp są otwarte na VPS/OVH, 3) czy „${host}” wskazuje na IP serwera. Zobacz „Pokaż logi bramki” — sekcja COTURN.`);
    } catch (e) { setTurnResult("Błąd testu TURN: " + (e && e.message ? e.message : String(e))); }
  };
  const input = "w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm";
  const fetchIceLog = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/sip/ice-log");
      setIceLog(`===== ASTERISK (ICE/DTLS) =====\n${data.asterisk}\n\n===== COTURN =====\n${data.coturn}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Nie udało się pobrać logu"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-400">Wpisz dane od operatora VoIP: <b>serwer SIP</b>, <b>login</b> i <b>hasło</b>. Po zapisaniu i włączeniu po prawej stronie ekranu pojawi się słuchawka do dzwonienia i odbierania.</p>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="sip-enabled" className="accent-cyan-500" />
        Włącz telefon VoIP (słuchawka)
      </label>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={cfg.call_flyto !== false} onChange={(e) => set("call_flyto", e.target.checked)} data-testid="sip-call-flyto" className="accent-cyan-500" />
        Przybliż mapę i podświetl klienta przy połączeniu przychodzącym
      </label>
      {cfg.turn_host && (
        <div className="text-[11px] text-emerald-300 bg-emerald-900/10 border border-emerald-800/40 rounded p-2" data-testid="sip-turn-status">
          Serwer TURN aktywny: <code>{cfg.turn_host}:3478</code> (poprawia audio przy trudnym NAT / symetrycznym). Konfigurowany automatycznie przez „Zapisz i skonfiguruj bramkę".
        </div>
      )}
      <div className="text-xs">
        <div className="font-semibold text-slate-300 mb-1">Tryb połączenia</div>
        <div className="grid grid-cols-1 gap-1">
          <label className={`flex items-start gap-2 rounded border px-2 py-1.5 cursor-pointer ${cfg.mode !== "gateway" ? "border-cyan-600 bg-cyan-900/20" : "border-slate-700"}`}>
            <input type="radio" name="sipmode" checked={cfg.mode !== "gateway"} onChange={() => set("mode", "direct")} data-testid="sip-mode-direct" className="mt-0.5 accent-cyan-500" />
            <span><b>Bezpośrednio (WSS operatora)</b> — jak teraz. Wymaga, aby operator udostępniał SIP przez WebSocket (WSS).</span>
          </label>
          <label className={`flex items-start gap-2 rounded border px-2 py-1.5 cursor-pointer ${cfg.mode === "gateway" ? "border-amber-600 bg-amber-900/20" : "border-slate-700"}`}>
            <input type="radio" name="sipmode" checked={cfg.mode === "gateway"} onChange={() => set("mode", "gateway")} data-testid="sip-mode-gateway" className="mt-0.5 accent-amber-500" />
            <span><b>Przez bramkę na moim serwerze</b> — gdy operator daje tylko port 5060. Uruchom dołączoną bramkę Asterisk (folder <code>gateway/</code>) i wpisz jej adres WSS.</span>
          </label>
        </div>
      </div>
      {cfg.mode === "gateway" && (
        <div className="text-[11px] text-amber-300 bg-amber-900/10 border border-amber-800/40 rounded p-2" data-testid="sip-gateway-note">
          Bramka jest darmowa (Asterisk, licencja GPL) i dozwolona komercyjnie. Wpisz w „Serwer SIP" adres swojego serwera z bramką, a w „Adres WebSocket" (Zaawansowane) zwykle <code>wss://twoj-serwer:8089/ws</code>. Pełna instrukcja: <code>gateway/README.md</code>.
        </div>
      )}
      <div>
        <label className="text-xs font-semibold text-slate-300 mb-1 block">{cfg.mode === "gateway" ? "Serwer bramki (adres WSS)" : "Serwer SIP"}</label>
        <input value={cfg.server} onChange={(e) => set("server", e.target.value)} placeholder={cfg.mode === "gateway" ? "np. fiberpass.pl (serwer z bramką)" : "pbx.twojoperator.pl"} data-testid="sip-server" className={input} autoComplete="off" />
      </div>
      {cfg.mode === "gateway" && (
        <div>
          <label className="text-xs font-semibold text-slate-300 mb-1 block">Serwer operatora (PBX)</label>
          <input value={cfg.operator_server} onChange={(e) => set("operator_server", e.target.value)} placeholder="pbx002.enteroperator.pl" data-testid="sip-operator-server" className={input} autoComplete="off" />
          <p className="text-[11px] text-slate-500 mt-1">Adres serwera SIP operatora (port 5060). Bramka zarejestruje się tu Twoim loginem i hasłem.</p>
        </div>
      )}
      <div>
        <label className="text-xs font-semibold text-slate-300 mb-1 block">Login (nr konta / użytkownik)</label>
        <input value={cfg.login} onChange={(e) => set("login", e.target.value)} placeholder="100" data-testid="sip-login" className={input} autoComplete="off" />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-300 mb-1 block">Hasło SIP</label>
        <input type="password" value={cfg.password} onChange={(e) => set("password", e.target.value)} data-testid="sip-pass" className={input} autoComplete="new-password" />
      </div>
      <button type="button" onClick={() => setAdv((a) => !a)} data-testid="sip-adv-toggle" className="text-xs text-cyan-400 hover:underline">
        {adv ? "Ukryj ustawienia zaawansowane" : "Ustawienia zaawansowane (opcjonalnie)"}
      </button>
      {adv && (
        <div className="space-y-3 border-l-2 border-slate-700 pl-3">
          <div>
            <label className="text-xs font-semibold text-slate-300 mb-1 block">Adres WebSocket (WSS)</label>
            <input value={cfg.ws_url} onChange={(e) => set("ws_url", e.target.value)} placeholder={cfg.server ? `wss://${cfg.server}:8089/ws` : "wss://serwer:8089/ws"} data-testid="sip-ws" className={input} autoComplete="off" />
            <p className="text-[11px] text-slate-500 mt-1">Zostaw puste — użyjemy domyślnie <code>wss://{cfg.server || "serwer"}:8089/ws</code>. Zmień tylko, jeśli operator poda inny adres/port.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 mb-1 block">Nazwa wyświetlana</label>
            <input value={cfg.display_name} onChange={(e) => set("display_name", e.target.value)} placeholder="Dyspozytor" data-testid="sip-name" className={input} />
          </div>
          {cfg.mode === "gateway" && (
            <div>
              <label className="text-xs font-semibold text-slate-300 mb-1 block">Publiczny adres IP (dla NAT)</label>
              <input value={cfg.public_ip} onChange={(e) => set("public_ip", e.target.value)} placeholder="auto — wykryjemy sami" data-testid="sip-public-ip" className={input} autoComplete="off" />
              <p className="text-[11px] text-slate-500 mt-1">Zostaw puste — wykryjemy publiczne IP automatycznie i (jeśli serwer jest za NAT) ustawimy je w bramce. Wpisz ręcznie tylko, gdy auto-wykrywanie zawiedzie.</p>
            </div>
          )}
        </div>
      )}
      <button onClick={submit} disabled={busy} data-testid="sip-save"
        className="w-full bg-blue-600 hover:bg-blue-700 rounded px-3 py-2 text-sm font-medium disabled:opacity-50">
        {busy ? "Zapisywanie…" : "Zapisz konto SIP"}
      </button>
      {cfg.mode === "gateway" && (
        <button onClick={applyGateway} disabled={busy} data-testid="sip-gateway-apply"
          className="w-full bg-amber-600 hover:bg-amber-500 text-slate-900 rounded px-3 py-2 text-sm font-semibold disabled:opacity-50">
          {busy ? "Pracuję…" : "Zapisz i skonfiguruj bramkę"}
        </button>
      )}
      {cfg.mode === "gateway" && (
        <button onClick={showLogs} disabled={busy} data-testid="sip-gateway-logs"
          className="w-full bg-slate-700 hover:bg-slate-600 rounded px-3 py-2 text-xs font-medium disabled:opacity-50">
          {busy ? "…" : "Pokaż logi bramki (diagnostyka połączeń)"}
        </button>
      )}
      {cfg.mode === "gateway" && (
        <button onClick={testTurn} disabled={busy} data-testid="sip-turn-test"
          className="w-full bg-emerald-700 hover:bg-emerald-600 rounded px-3 py-2 text-xs font-semibold disabled:opacity-50">
          Testuj TURN (z tej przeglądarki)
        </button>
      )}
      {turnResult && (
        <div data-testid="sip-turn-test-result" className={`text-[11px] rounded p-2 border ${turnResult.startsWith("✔") ? "text-emerald-300 bg-emerald-900/10 border-emerald-800/40" : "text-amber-200 bg-amber-900/10 border-amber-800/40"}`}>{turnResult}</div>
      )}
      {cfg.mode === "gateway" && (
        <button onClick={fetchIceLog} disabled={busy} data-testid="sip-ice-log"
          className="w-full bg-indigo-700 hover:bg-indigo-600 rounded px-3 py-2 text-xs font-semibold disabled:opacity-50">
          {busy ? "…" : "Zbierz log ICE/DTLS (kliknij PO nieudanej próbie)"}
        </button>
      )}
      {iceLog && (
        <pre data-testid="sip-ice-log-output" className="max-h-72 overflow-auto bg-black/60 border border-slate-700 rounded p-2 text-[10px] leading-tight text-slate-300 whitespace-pre-wrap">{iceLog}</pre>
      )}
      {cfg.mode === "gateway" && (
        <button onClick={enableDebug} disabled={busy} data-testid="sip-gateway-debug"
          className="w-full bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded px-3 py-2 text-xs font-medium disabled:opacity-50">
          {busy ? "…" : "Włącz debug połączeń (SIP/RTP) — przed testem"}
        </button>
      )}
      {logs !== null && (
        <pre data-testid="sip-gateway-logs-output" className="max-h-64 overflow-auto bg-black/60 border border-slate-700 rounded p-2 text-[10px] leading-tight text-slate-300 whitespace-pre-wrap">{logs}</pre>
      )}
    </div>
  );
}
